import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { LifeBuoy, RefreshCw, ShieldAlert, Loader2, CheckCircle2, BookOpen, Send, AlertTriangle } from 'lucide-react';
import { supportApi, kbApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import { TableScroll } from '../../components/ResponsiveTable';
import usePageTitle from '../../hooks/usePageTitle';
import { cn } from '@/lib/utils';

/*
 * A2: the support-ticket queue (design: NecroMancerV2/docs/ai/03_TICKETING.md).
 *
 * One row = ONE ROOT CAUSE (fingerprint), not one report: 40 customers hitting the same bug are
 * one ticket with affectedUsers=40. The queue is sorted by the server (most customers first) so
 * the top row is always "what hurts the most people right now".
 *
 * Server-side gates: ROLE_ADMIN + the UIAPI_SUPPORT_UI flag; ingest itself is a separate flag
 * (SUPPORT_TICKETING_INGEST). This page reports BOTH states honestly instead of showing an
 * inexplicably empty queue.
 *
 * A6 (feedback loop, behind UIAPI_FEEDBACK_LOOP): a KNOWLEDGE-GAP ticket (component KB) is
 * answered here, not "moved" - the Review panel shows the customer's question as reference, the
 * draft reply with the machine gates' verdict (leaks, PII, unknown settings, probe), and who the
 * reply will reach. "Send reply" is the ONLY way such a ticket becomes answered; it delivers
 * into every asker's conversation. The learned-KB entry is a SEPARATE artifact with a separate
 * approval, and its served question must be written by a human - never pasted from a customer.
 *
 * Aurora (2026-08-23): semantic tokens only (success / warning / danger / info / accent), glass
 * cards, the shared PageHeader, and a row list instead of the table below md. The detail stays a
 * Dialog (that is what it does); it is full-width on phones.
 */

const SEVERITY_STYLE = {
  critical: 'bg-danger/20 text-danger border-danger/40',
  high: 'bg-warning/20 text-warning border-warning/40',
  medium: 'bg-warning/10 text-warning border-warning/25',
  low: 'bg-muted/60 text-muted-foreground border-border',
};

const STATUS_STYLE = {
  new: 'bg-primary/20 text-primary',
  triaged: 'bg-info/20 text-info',
  analyzing: 'bg-accent/20 text-accent',
  'awaiting-approval': 'bg-warning/20 text-warning',
  'needs-info': 'bg-warning/20 text-warning',
  approved: 'bg-success/20 text-success',
  'fix-merged': 'bg-success/20 text-success',
  deployed: 'bg-success/20 text-success',
  answered: 'bg-success/20 text-success',
  verifying: 'bg-primary/10 text-primary',
  verified: 'bg-success/25 text-success',
  reopened: 'bg-danger/20 text-danger',
  'wont-fix': 'bg-muted/60 text-muted-foreground',
  duplicate: 'bg-muted/60 text-muted-foreground',
};

const MUTED_PILL = 'bg-muted/60 text-muted-foreground';
const LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const FIELD = 'bg-secondary/60 border-border';
const SELECT = 'h-8 rounded-md border border-border bg-secondary/60 px-2 text-xs text-foreground';

const ENTRY_KINDS = ['answer', 'not-supported', 'expected-behaviour', 'needs-code-change'];

const errText = (e) => e?.response?.data?.error || e?.message || 'The server did not answer.';

/* A status / severity chip: the outline variant so the tint classes own the colours. */
function Pill({ className, children, ...rest }) {
  return <Badge variant="outline" className={cn('rounded-full border-transparent', className)} {...rest}>{children}</Badge>;
}

/* One skeleton bone; pages compose a few into the shape of what is loading. */
function Bone({ className }) {
  return <div className={cn('animate-pulse rounded-xl bg-secondary/60', className)} aria-hidden />;
}

/* An error card in Aurora: the message as-is, plus a retry when the caller has one. */
function ErrorCard({ children, onRetry, testId }) {
  return (
    <div className="aurora-card p-6 text-center sm:p-8" role="alert" data-testid={testId}>
      <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
        <ShieldAlert className="h-5 w-5" />
      </span>
      <p className="mx-auto mt-3 max-w-md text-sm text-foreground/90 break-words">{children}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} className="tap mt-4 rounded-full">
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      )}
    </div>
  );
}

/** One artifact's gate verdict, with the spans a reviewer needs to see. */
function GateReport({ report, label }) {
  if (!report) return null;
  const ok = report.publishable;
  return (
    <div className={cn('rounded-lg border p-2 text-xs', ok ? 'border-success/40 bg-success/5' : 'border-danger/40 bg-danger/5')}
         data-testid={`gates-${label}`}>
      <div className={ok ? 'text-success' : 'text-danger'}>
        {label}: {ok ? 'publishable' : `BLOCKED — ${report.failedGates.join(', ')}`}
      </div>
      {report.leaks?.length > 0 && (
        <ul className="mt-1 list-inside list-disc break-words text-danger/90">
          {report.leaks.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
      {report.unknownSettings?.length > 0 && (
        <div className="mt-1 break-words text-danger/90">settings that do not exist: {report.unknownSettings.join(', ')}</div>
      )}
      {report.unsafe && <div className="mt-1 text-danger/90">looks like a credential or personal data survived</div>}
      {report.probe && report.probe !== 'none' && (
        <div className="mt-1 text-danger/90">instruction-shaped text (probe severity {report.probe})</div>
      )}
      {report.tenantSpecific && (
        <div className="mt-1 flex items-start gap-1 text-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>warning: names a slot or recommends a value — this goes to more than one customer</span>
        </div>
      )}
      {report.settingsNamed?.length > 0 && (
        <div className="mt-1 break-words text-muted-foreground">settings named: {report.settingsNamed.join(', ')}</div>
      )}
    </div>
  );
}

/** The A6 review panel for one knowledge-gap ticket: reply + entries, two approvals. */
function ReviewPanel({ ticketId, onChanged }) {
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState('');
  const [replyReport, setReplyReport] = useState(null);
  // Seed the editor from the draft ONCE per ticket. Re-seeding on every refetch (approving an
  // entry, a background refresh) silently threw away what the reviewer had typed, so the text
  // that got sent was not the text on screen (review, 2026-08-21).
  const seededFor = useRef(null);
  const [entryEdits, setEntryEdits] = useState({}); // id -> {question, answer, kind, linkedTicketId}
  const [convert, setConvert] = useState({ component: '', errorCode: '', duplicateOf: '' });

  const reviewQuery = useQuery({
    queryKey: ['kb-review', ticketId],
    queryFn: () => kbApi.review(ticketId),
    retry: false,
  });
  const review = reviewQuery.data?.data;

  useEffect(() => {
    if (review && seededFor.current !== review.ticketId) {
      seededFor.current = review.ticketId;
      setReplyText(review.replyDraft || review.customerSummary || '');
      setReplyReport(review.replyGates || null);
    }
  }, [review]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['kb-review', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['kb-entries'] });
    onChanged?.();
  };

  const checkMutation = useMutation({
    mutationFn: () => kbApi.check(ticketId, replyText),
    onSuccess: (res) => setReplyReport(res.data.report),
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });
  const replyMutation = useMutation({
    mutationFn: () => kbApi.reply(ticketId, replyText),
    onSuccess: (res) => {
      toast.success(`${res.data.id} answered — delivered into ${res.data.delivered} conversation(s)`);
      refresh();
    },
    onError: (e) => {
      if (e.response?.data?.report) setReplyReport(e.response.data.report);
      toast.error(e.response?.data?.error || e.message);
    },
  });
  const [entryReports, setEntryReports] = useState({}); // id -> the server's gate report
  const approveMutation = useMutation({
    mutationFn: ({ id, body }) => kbApi.approve(id, body),
    onSuccess: (res) => {
      setEntryReports((s) => ({ ...s, [res.data.id]: null }));
      toast.success(`${res.data.id} approved — now served to the assistant`);
      refresh();
    },
    onError: (e, vars) => {
      // The 422 carries WHICH gate failed and where; showing only the toast left the reviewer
      // guessing (review). pasteThrough is the one that needs words, not spans.
      if (e.response?.data?.report) {
        setEntryReports((s) => ({ ...s, [vars.id]: e.response.data.report }));
      }
      toast.error(e.response?.data?.error || e.message);
    },
  });
  const rejectMutation = useMutation({
    mutationFn: (id) => kbApi.reject(id),
    onSuccess: () => { toast.success('Entry rejected'); refresh(); },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });
  const convertMutation = useMutation({
    mutationFn: (body) => supportApi.convert(ticketId, body),
    onSuccess: (res) => {
      toast.success(res.data.duplicateOf
        ? `${res.data.id} merged into ${res.data.duplicateOf}`
        : `${res.data.id} is now ${res.data.component} / ${res.data.errorCode}`);
      refresh();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  if (reviewQuery.isPending) {
    return (
      <div className="space-y-2 border-t border-border pt-4" aria-busy="true">
        <Bone className="h-4 w-40" />
        <Bone className="h-20 w-full" />
        <p className="text-sm text-muted-foreground">Loading review…</p>
      </div>
    );
  }
  if (reviewQuery.isError) {
    return <div className="border-t border-border pt-4 text-sm text-warning">Review unavailable: {reviewQuery.error?.response?.data?.error || reviewQuery.error?.message}</div>;
  }
  if (!review) return null;

  // An approved reply EXISTS - which stays true through verifying/verified/reopened, not just
  // while the status literally reads 'answered'. Keying on the status alone hid the sent reply
  // and offered the editor again the moment the verification sweep advanced the ticket, i.e. for
  // the whole rest of its life (review, 2026-08-21). replyApprovedBy is the server's own marker.
  const answered = !!review.replyApprovedBy && !!review.customerSummary;
  const audience = review.deliverTo || {};
  const edit = (id) => entryEdits[id] || {};

  return (
    <div className="space-y-4 border-t border-border pt-4" data-testid="review-panel">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <BookOpen className="h-4 w-4 text-success" />
        <span className="font-heading text-base font-semibold">Knowledge-gap review</span>
        <span className="text-xs text-muted-foreground tnum">
          reply reaches {audience.chats ?? 0} conversation(s) · {audience.tenants ?? 0} customer(s)
        </span>
      </div>

      <div>
        <div className={cn(LABEL, 'mb-1')}>Customer's question (reference only — never served)</div>
        <pre className="whitespace-pre-wrap break-words rounded-lg bg-secondary/60 p-3 text-sm">{review.customerQuestion}</pre>
        {review.rationale && (
          <div className="mt-1 text-xs text-muted-foreground">agent rationale ({review.confidence || '?'}): {review.rationale}</div>
        )}
        {review.suggestConvert && (
          <div className="mt-1 text-xs text-warning">The agent attached a diff: this may be a code defect, not a knowledge gap — consider converting.</div>
        )}
      </div>

      {/* Artifact 1: the reply */}
      <div className="space-y-2">
        <div className={LABEL}>Artifact 1 — the reply (one customer, once per asker)</div>
        {answered ? (
          <div className="text-sm text-success">
            Answered {review.answeredAt ? `at ${review.answeredAt}` : ''} by {review.replyApprovedBy || '?'}
            {review.status !== 'answered' && <span className="text-muted-foreground"> (now {review.status})</span>}:
            <pre className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-secondary/60 p-3 text-sm text-foreground">{review.customerSummary}</pre>
          </div>
        ) : (
          <>
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={5}
                      className={cn(FIELD, 'text-sm placeholder:text-muted-foreground/70')} data-testid="reply-text"
                      placeholder="What to CHECK and why, in plain language. No file names, no account-specific values, no rate/limit numbers." />
            <GateReport report={replyReport} label="reply" />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="tap"
                      disabled={!replyText.trim() || checkMutation.isPending}
                      onClick={() => checkMutation.mutate()}>
                Check gates
              </Button>
              <Button size="sm" className="tap shadow-glow"
                      disabled={!replyText.trim() || replyMutation.isPending || !review.isKbGap}
                      onClick={() => {
                        if (!window.confirm(`Send this reply into ${audience.chats ?? 0} conversation(s) (${audience.tenants ?? 0} customer(s))? This cannot be un-sent.`)) return;
                        replyMutation.mutate();
                      }}
                      data-testid="send-reply">
                {replyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send reply to {audience.chats ?? 0} chat(s)
              </Button>
            </div>
            {!review.isKbGap && (
              <div className="text-xs text-warning">This is a code ticket now — it ships by fix-merged → deployed. Convert it back to a KB gap to answer it.</div>
            )}
          </>
        )}
      </div>

      {/* Artifact 2: learned entries */}
      <div className="space-y-3">
        <div className={LABEL}>Artifact 2 — learned KB entry (every customer, forever)</div>
        {(review.entries || []).length === 0 && (
          <div className="text-xs text-muted-foreground">No draft entry yet. The agent's kbDraft, or a staff seed on the Learned KB tab, creates one.</div>
        )}
        {(review.entries || []).map((e) => (
          <div key={e.id} className="space-y-2 rounded-xl border border-border bg-secondary/30 p-3" data-testid={`entry-${e.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono text-primary">{e.id}</span>
              <Pill className={e.status === 'approved' ? 'bg-success/20 text-success' : MUTED_PILL}>{e.status}</Pill>
              <span className="text-xs text-muted-foreground">drafted by {e.draftedBy}</span>
            </div>
            {e.status === 'draft' || e.status === 'flagged' ? (
              <>
                <Input value={edit(e.id).question ?? e.question ?? ''} maxLength={200}
                       onChange={(ev) => setEntryEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), question: ev.target.value } }))}
                       placeholder="Write the question in your own words (required — the customer's text is never served)"
                       className={cn(FIELD, 'text-sm placeholder:text-muted-foreground/70')} data-testid={`entry-question-${e.id}`} />
                <Textarea value={edit(e.id).answer ?? e.answer ?? ''} rows={4}
                          onChange={(ev) => setEntryEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), answer: ev.target.value } }))}
                          className={cn(FIELD, 'text-sm')} />
                <div className="flex flex-wrap items-center gap-2">
                  <select value={edit(e.id).kind ?? e.kind ?? 'answer'}
                          onChange={(ev) => setEntryEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), kind: ev.target.value } }))}
                          className={SELECT}>
                    {ENTRY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  {(edit(e.id).kind ?? e.kind) === 'needs-code-change' && (
                    <Input value={edit(e.id).linkedTicketId ?? e.linkedTicketId ?? ''} placeholder="tracked as ticket NM-XXXX"
                           onChange={(ev) => setEntryEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), linkedTicketId: ev.target.value } }))}
                           className={cn(FIELD, 'h-8 w-full text-xs sm:w-44')} />
                  )}
                  <Button size="sm" className="tap"
                          disabled={approveMutation.isPending || !(edit(e.id).question ?? e.question)}
                          onClick={() => approveMutation.mutate({ id: e.id, body: {
                            question: edit(e.id).question ?? e.question,
                            answer: edit(e.id).answer ?? e.answer,
                            kind: edit(e.id).kind ?? e.kind,
                            linkedTicketId: edit(e.id).linkedTicketId ?? e.linkedTicketId,
                          } })}
                          data-testid={`approve-${e.id}`}>
                    Approve entry
                  </Button>
                  <Button size="sm" variant="outline" className="tap"
                          disabled={rejectMutation.isPending} onClick={() => rejectMutation.mutate(e.id)}>
                    Reject
                  </Button>
                </div>
                {(entryReports[e.id]?.question || e.gates?.question) && (
                  <GateReport report={entryReports[e.id]?.question || e.gates?.question} label="entry question" />
                )}
                {(entryReports[e.id]?.answer || e.gates?.answer) && (
                  <GateReport report={entryReports[e.id]?.answer || e.gates?.answer} label="entry answer" />
                )}
                {entryReports[e.id]?.pasteThrough && (
                  <div className="rounded-lg border border-danger/40 bg-danger/5 p-2 text-xs text-danger">
                    BLOCKED — that is the customer's own text. Write the question in your own words:
                    it is served to every other customer who asks something similar.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-sm break-words"><span className="text-muted-foreground">Q:</span> {e.question}</div>
                <div className="whitespace-pre-wrap break-words text-sm"><span className="text-muted-foreground">A:</span> {e.answer}</div>
                <div className="text-xs text-muted-foreground">approved by {e.approvedBy} at {e.approvedAt} · kind {e.kind}</div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Convert / merge */}
      {!answered && (
        <div className="space-y-2">
          <div className={LABEL}>Not a knowledge gap?</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={convert.component} onChange={(e) => setConvert({ ...convert, component: e.target.value })}
                   placeholder="component (BC/BM/SERVER/BRIDGE)" className={cn(FIELD, 'h-8 w-full text-xs sm:w-44')} />
            <Input value={convert.errorCode} onChange={(e) => setConvert({ ...convert, errorCode: e.target.value })}
                   placeholder="errorCode" className={cn(FIELD, 'h-8 w-full text-xs sm:w-44')} />
            <Button size="sm" variant="outline" className="tap"
                    disabled={!convert.component || !convert.errorCode || convertMutation.isPending}
                    onClick={() => convertMutation.mutate({ component: convert.component, errorCode: convert.errorCode })}>
              Convert to bug
            </Button>
            <span className="text-xs text-muted-foreground">or</span>
            <Input value={convert.duplicateOf} onChange={(e) => setConvert({ ...convert, duplicateOf: e.target.value })}
                   placeholder="merge into NM-XXXX" className={cn(FIELD, 'h-8 w-full text-xs sm:w-36')} />
            <Button size="sm" variant="outline" className="tap"
                    disabled={!convert.duplicateOf || convertMutation.isPending}
                    onClick={() => convertMutation.mutate({ duplicateOf: convert.duplicateOf })}>
              Merge
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Converting keeps the id and every report; merging moves the reports and the askers' conversations to the survivor.</p>
        </div>
      )}
    </div>
  );
}

/** One mutation shape for every entry action: toast the outcome, refresh the lists. */
function useKbAction(fn, okMsg, refresh) {
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { toast.success(okMsg); refresh(); },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });
}

/* One number in the KB metrics card. */
function Metric({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <p className={LABEL}>{label}</p>
      <p className="mt-1 break-words font-heading text-lg font-semibold leading-tight tnum">
        {value}
        {sub && <span className="ml-1 text-xs font-normal text-muted-foreground">({sub})</span>}
      </p>
    </div>
  );
}

/** The Learned KB tab: every entry by status, staff seeding, staleness, metrics. */
function LearnedKbTab() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('approved');
  const [seed, setSeed] = useState({ answer: '', kind: 'answer' });
  const [edits, setEdits] = useState({});

  const entriesQuery = useQuery({
    queryKey: ['kb-entries', status],
    queryFn: () => kbApi.entries(status ? { status } : {}),
    retry: false,
  });
  const metricsQuery = useQuery({ queryKey: ['kb-metrics'], queryFn: () => kbApi.metrics(), retry: false });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['kb-entries'] });
    queryClient.invalidateQueries({ queryKey: ['kb-metrics'] });
  };
  const seedMutation = useMutation({
    mutationFn: (body) => kbApi.seed(body),
    onSuccess: () => {
      setSeed({ answer: '', kind: 'answer' });   // cleared only once the server has it
      setStatus('draft');
      toast.success('Draft created — give it a question and approve it');
      refresh();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });
  const approveMutation = useKbAction(({ id, body }) => kbApi.approve(id, body), 'Entry approved', refresh);
  const rejectMutation = useKbAction((id) => kbApi.reject(id), 'Entry rejected', refresh);
  const flagMutation = useKbAction(({ id, reason }) => kbApi.flag(id, reason), 'Entry flagged — served with a hedge until re-confirmed', refresh);
  const confirmMutation = useKbAction((id) => kbApi.confirm(id), 'Entry re-confirmed', refresh);
  const retireMutation = useKbAction((id) => kbApi.retire(id), 'Entry retired', refresh);

  const rows = entriesQuery.data?.data || [];
  const m = metricsQuery.data?.data;
  const edit = (id) => edits[id] || {};
  const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);

  return (
    <div className="space-y-4" data-testid="learned-kb-tab">
      {m && (
        <Card className="aurora-card">
          <CardContent className="grid grid-cols-1 gap-4 p-4 text-sm xs:grid-cols-2 sm:p-5 xl:grid-cols-4">
            <Metric label="escalation rate (7d)" value={pct(m.escalationRate7d)} sub={`${m.escalations7d}/${m.answers7d}`} />
            <Metric label="answers citing learned (7d)" value={pct(m.answersCitingLearned7d)} />
            <Metric label="unrated share (7d)" value={pct(m.unratedShare7d)} />
            <Metric label="median escalation → answer" value={`${m.medianEscalationToAnswerHours ?? '—'} h`} />
            <Metric label="approval edit rate" value={pct(m.approvalEditRate)} />
            <Metric label="rejected for leak (90d)" value={pct(m.rejectedForLeakRate90d)} sub={`${m.gateFails90d} fails / ${m.gatePasses90d} passes`} />
            <Metric label="repeat askers after answer (90d)" value={m.repeatAskersAfterAnswer90d} />
            <Metric label="entries" value={`${m.entriesApproved} approved · ${m.entriesDraft} draft · ${m.entriesFlagged} flagged`} />
            {m.notMeasured?.length > 0 && (
              <div className="text-xs text-muted-foreground xs:col-span-2 xl:col-span-4">not measured: {m.notMeasured.join(' · ')}</div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="aurora-card">
        <CardContent className="space-y-2 p-4 sm:p-5">
          <div className={LABEL}>Seed an entry (you write the question at approval)</div>
          <div className="flex flex-wrap gap-2">
            <Textarea value={seed.answer} onChange={(e) => setSeed({ ...seed, answer: e.target.value })} rows={2}
                      placeholder="customer-safe answer" className={cn(FIELD, 'min-w-0 flex-1 basis-full text-sm placeholder:text-muted-foreground/70 sm:basis-auto sm:min-w-[16rem]')} />
            <select value={seed.kind} onChange={(e) => setSeed({ ...seed, kind: e.target.value })}
                    className={cn(SELECT, 'h-9')}>
              {ENTRY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <Button size="sm" variant="outline" className="tap h-9" disabled={!seed.answer.trim() || seedMutation.isPending}
                    onClick={() => seedMutation.mutate(seed)}>
              Create draft
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-1.5">
        {['draft', 'approved', 'flagged', 'rejected', 'retired', ''].map((s) => (
          <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'}
                  className="tap rounded-full" aria-pressed={status === s}
                  onClick={() => setStatus(s)}>
            {s || 'all'}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {entriesQuery.isPending && (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => <Bone key={i} className="h-24 w-full" />)}
          </div>
        )}
        {entriesQuery.isError && (
          <ErrorCard onRetry={() => entriesQuery.refetch()} testId="kb-entries-error">
            Could not load entries: {errText(entriesQuery.error)}
          </ErrorCard>
        )}
        {!entriesQuery.isPending && !entriesQuery.isError && rows.length === 0 && (
          <div className="aurora-card aurora-card-dashed p-6 text-center text-sm text-muted-foreground sm:p-8" data-testid="kb-entries-empty">
            No entries{status ? ` in '${status}'` : ''}.
          </div>
        )}
        {rows.map((e) => (
          <Card key={e.id} className="aurora-card">
            <CardContent className="space-y-2 p-3 text-sm sm:p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-primary">{e.id}</span>
                <Pill className={e.status === 'approved' ? 'bg-success/20 text-success' : e.status === 'flagged' ? 'bg-warning/20 text-warning' : MUTED_PILL}>{e.status}</Pill>
                <span className="text-xs text-muted-foreground">{e.kind}{e.sourceTicketId ? ` · from ${e.sourceTicketId}` : ' · staff-seeded'}</span>
                {e.needsReconfirm && <Pill className="bg-warning/20 text-warning">needs re-confirmation</Pill>}
                {e.staleReason && <span className="text-xs text-warning">{e.staleReason}</span>}
              </div>
              {e.customerQuestion && (
                <div className="break-words text-xs text-muted-foreground">customer asked: <i>{e.customerQuestion}</i> (reference only)</div>
              )}
              {(e.status === 'draft' || e.status === 'flagged') ? (
                <div className="space-y-2">
                  <Input value={edit(e.id).question ?? e.question ?? ''} maxLength={200}
                         onChange={(ev) => setEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), question: ev.target.value } }))}
                         placeholder="Write the question in your own words (required)" className={cn(FIELD, 'text-sm placeholder:text-muted-foreground/70')} />
                  <Textarea value={edit(e.id).answer ?? e.answer ?? ''} rows={3}
                            onChange={(ev) => setEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), answer: ev.target.value } }))}
                            className={cn(FIELD, 'text-sm')} />
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={edit(e.id).kind ?? e.kind ?? 'answer'}
                            onChange={(ev) => setEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), kind: ev.target.value } }))}
                            className={SELECT}>
                      {ENTRY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                    {(edit(e.id).kind ?? e.kind) === 'needs-code-change' && (
                      <Input value={edit(e.id).linkedTicketId ?? e.linkedTicketId ?? ''} placeholder="tracked as NM-XXXX"
                             onChange={(ev) => setEdits((s) => ({ ...s, [e.id]: { ...edit(e.id), linkedTicketId: ev.target.value } }))}
                             className={cn(FIELD, 'h-8 w-full text-xs sm:w-40')} />
                    )}
                    <Button size="sm" className="tap"
                            disabled={!(edit(e.id).question ?? e.question) || approveMutation.isPending}
                            onClick={() => approveMutation.mutate({ id: e.id, body: {
                              question: edit(e.id).question ?? e.question,
                              answer: edit(e.id).answer ?? e.answer,
                              kind: edit(e.id).kind ?? e.kind,
                              linkedTicketId: edit(e.id).linkedTicketId ?? e.linkedTicketId,
                            } })}>
                      Approve
                    </Button>
                    {e.status === 'flagged' && (
                      <Button size="sm" variant="outline" className="tap" onClick={() => confirmMutation.mutate(e.id)}>Still correct — confirm</Button>
                    )}
                    <Button size="sm" variant="outline" className="tap" onClick={() => rejectMutation.mutate(e.id)}>Reject</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="break-words"><span className="text-muted-foreground">Q:</span> {e.question}</div>
                  <div className="whitespace-pre-wrap break-words"><span className="text-muted-foreground">A:</span> {e.answer}</div>
                  <div className="text-xs text-muted-foreground">
                    approved by {e.approvedBy} at {e.approvedAt} · confirmed {e.confirmedAt} · settings {e.derivedSettings}
                  </div>
                  {e.status === 'approved' && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="tap" onClick={() => confirmMutation.mutate(e.id)}>Confirm still correct</Button>
                      <Button size="sm" variant="outline" className="tap"
                              onClick={() => { const r = window.prompt('Why is this entry doubtful? (served with a hedge until re-confirmed)'); if (r !== null) flagMutation.mutate({ id: e.id, reason: r }); }}>
                        Flag
                      </Button>
                      <Button size="sm" variant="outline" className="tap" onClick={() => { if (window.confirm('Retire this entry? It stops being served.')) retireMutation.mutate(e.id); }}>Retire</Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* A warning banner for a flag that is off or only partly on. */
function FlagBanner({ children, testId }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning" role="status" data-testid={testId}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="min-w-0 flex-1 text-pretty">{children}</p>
    </div>
  );
}

/* The queue while it loads: a few row-shaped bones. */
function QueueSkeleton() {
  return (
    <div className="aurora-card space-y-3 p-4" aria-busy="true" data-testid="queue-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Bone className="h-4 w-16 shrink-0" />
          <Bone className="hidden h-4 w-24 shrink-0 sm:block" />
          <Bone className="h-5 w-14 shrink-0 rounded-full" />
          <Bone className="h-4 flex-1" />
        </div>
      ))}
      <p className="text-xs text-muted-foreground">Loading…</p>
    </div>
  );
}

export default function SupportTicketsPage() {
  usePageTitle('Support tickets');
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [tab, setTab] = useState('tickets');

  const infoQuery = useQuery({ queryKey: ['support-info'], queryFn: () => supportApi.info(), retry: false });
  const info = infoQuery.data?.data;
  const uiEnabled = info?.uiEnabled === true;
  const loopEnabled = info?.feedbackLoopEnabled === true;

  const queueQuery = useQuery({
    queryKey: ['support-queue', statusFilter],
    queryFn: () => supportApi.queue(statusFilter ? { status: statusFilter } : {}),
    enabled: uiEnabled,
    refetchInterval: 30000,
    retry: false,
  });

  const detailQuery = useQuery({
    queryKey: ['support-ticket', openTicketId],
    queryFn: () => supportApi.detail(openTicketId),
    enabled: uiEnabled && !!openTicketId,
    retry: false,
  });

  const transitionMutation = useMutation({
    mutationFn: ({ id, status, fixedInVersion, duplicateOf }) =>
      supportApi.setStatus(id, { status, ...(fixedInVersion ? { fixedInVersion } : {}), ...(duplicateOf ? { duplicateOf } : {}) }),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`${d.id} → ${d.status}`
        + (d.notifyCustomer ? ` — notify ${d.affectedEmails.length} customer(s)` : '')
        + (d.delivered ? ` — delivered into ${d.delivered} conversation(s)` : ''));
      queryClient.invalidateQueries({ queryKey: ['support-queue'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket'] });
      queryClient.invalidateQueries({ queryKey: ['kb-review'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const verifyMutation = useMutation({
    mutationFn: () => supportApi.runVerification(),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`Verification sweep: ${d.checked} checked, ${d.verified.length} verified, `
        + `${d.reopened.length} reopened, ${d.stillVerifying.length} still inside the window`
        + (d.skipped?.length ? `, ${d.skipped.length} skipped (flag off)` : ''));
      queryClient.invalidateQueries({ queryKey: ['support-queue'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const tickets = queueQuery.data?.data || [];
  const detail = detailQuery.data?.data;

  if (infoQuery.isError) {
    const msg = infoQuery.error?.response?.data?.error || infoQuery.error?.message;
    return (
      <div className="space-y-4">
        <PageHeader icon={LifeBuoy} title="Support tickets" />
        <ErrorCard onRetry={() => infoQuery.refetch()} testId="support-unavailable">
          Support queue unavailable: {msg}
        </ErrorCard>
      </div>
    );
  }

  const actions = (
    <>
      {loopEnabled && (
        <div role="tablist" aria-label="Support views" className="inline-flex rounded-full bg-secondary/70 p-1 ring-1 ring-border">
          <Button size="sm" role="tab" aria-selected={tab === 'tickets'}
                  variant={tab === 'tickets' ? 'default' : 'ghost'}
                  className={cn('rounded-full', tab === 'tickets' && 'shadow-glow')}
                  onClick={() => setTab('tickets')}>Tickets</Button>
          <Button size="sm" role="tab" aria-selected={tab === 'kb'}
                  variant={tab === 'kb' ? 'default' : 'ghost'}
                  className={cn('rounded-full', tab === 'kb' && 'shadow-glow')}
                  onClick={() => setTab('kb')} data-testid="tab-learned-kb">
            <BookOpen className="h-4 w-4" /> Learned KB
          </Button>
        </div>
      )}
      <Button
        variant="outline" size="sm" className="tap rounded-full"
        onClick={() => verifyMutation.mutate()}
        disabled={!uiEnabled || verifyMutation.isPending || (info?.ingestEnabled === false && !loopEnabled)}
        title={info?.ingestEnabled === false && !loopEnabled
          ? 'Disabled: neither ingest nor the feedback loop is on, so silence is not being measured'
          : 'Post-deploy sweep: fingerprints silent for the whole window become verified; recurrences reopen. KB gaps are measured by ratings, crashes by ingest.'}
      >
        {verifyMutation.isPending
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <CheckCircle2 className="h-4 w-4" />}
        Run verification
      </Button>
      <Button variant="ghost" size="icon" className="tap rounded-full" onClick={() => queueQuery.refetch()} disabled={!uiEnabled}
              aria-label="Refresh the queue" title="Refresh the queue (auto-refreshes every 30 s)">
        <RefreshCw className={cn('h-4 w-4', queueQuery.isFetching && 'animate-spin text-primary')} />
      </Button>
    </>
  );

  const queueError = queueQuery.isError ? errText(queueQuery.error) : null;

  // Below md the table becomes one tappable row per ticket, inside the same glass card.
  const mobileRows = (
    <div className="space-y-2 p-3">
      {tickets.map((t) => (
        <button
          key={t.id} type="button"
          onClick={() => setOpenTicketId(t.id)}
          data-testid={`ticket-card-${t.id}`}
          className="tap w-full rounded-xl bg-secondary/40 p-3 text-left transition-colors hover:bg-secondary/70"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-primary">{t.id}</span>
            <Pill className={SEVERITY_STYLE[t.severity] || SEVERITY_STYLE.medium}>{t.severity}</Pill>
            <Pill className={STATUS_STYLE[t.status] || MUTED_PILL}>{t.status}</Pill>
          </div>
          <p className="mt-2 line-clamp-2 text-sm text-foreground/90">{t.displayMessage}</p>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">{t.component} · {t.errorCode}</span>
            <span className="shrink-0 tnum"><span className="font-semibold text-foreground">{t.affectedUsers}</span> users · {t.occurrences} reports</span>
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="support-page">
      <PageHeader
        icon={LifeBuoy}
        title="Support tickets"
        description={info ? `${info.openTickets} open · one row per root cause` : undefined}
        actions={actions}
      />

      {infoQuery.isPending && (
        <div className="space-y-3" aria-busy="true" data-testid="support-skeleton">
          <div className="flex flex-wrap gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => <Bone key={i} className="h-8 w-20 rounded-full" />)}
          </div>
          <QueueSkeleton />
        </div>
      )}

      {info && !uiEnabled && (
        <FlagBanner testId="support-ui-off">
          The support queue is disabled — turn on <code>UIAPI_SUPPORT_UI</code> on the Flags page.
        </FlagBanner>
      )}
      {info && uiEnabled && !info.ingestEnabled && (
        <FlagBanner testId="support-ingest-off">
          {info.ingestRolloutPercent > 0 ? (
            <>Ticket <b>ingest</b> is in partial rollout (<code>SUPPORT_TICKETING_INGEST</code> at{' '}
            {info.ingestRolloutPercent}%): roughly that share of accounts are filing tickets;
            the rest are not. Crash verification stays disabled until it is globally on.</>
          ) : (
            <>Ticket <b>ingest</b> is globally off (<code>SUPPORT_TICKETING_INGEST</code>): sessions
            are not filing tickets, so this queue only shows what was collected while it was on,
            and crash verification is disabled (silence is not being measured).</>
          )}
        </FlagBanner>
      )}

      {uiEnabled && tab === 'kb' && loopEnabled && <LearnedKbTab />}
      {/* Exhaustive by construction: if the KB tab is selected but the flag went off under it,
          fall through to the ticket table rather than rendering an empty page with no way back
          (the tab bar is itself behind the flag) - review, 2026-08-21. */}

      {uiEnabled && !(tab === 'kb' && loopEnabled) && (
        <>
          <div className="flex flex-wrap gap-1.5" data-testid="status-filters">
            <Button
              size="sm" variant={statusFilter === null ? 'default' : 'outline'}
              className="tap rounded-full" aria-pressed={statusFilter === null}
              onClick={() => setStatusFilter(null)}
            >
              Open queue
            </Button>
            {(info?.statuses || []).map((s) => (
              <Button
                key={s} size="sm"
                variant={statusFilter === s ? 'default' : 'outline'}
                className="tap rounded-full" aria-pressed={statusFilter === s}
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </Button>
            ))}
          </div>

          {queueQuery.isPending ? (
            <QueueSkeleton />
          ) : tickets.length === 0 ? (
            queueError ? (
              <ErrorCard onRetry={() => queueQuery.refetch()} testId="queue-error">
                Could not load the queue: {queueError}
              </ErrorCard>
            ) : (
              <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="queue-empty">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success ring-1 ring-success/20">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <h3 className="mt-4">No tickets — that is the good outcome.</h3>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
                  {statusFilter ? `Nothing is in '${statusFilter}' right now.` : 'No root cause is hurting anyone right now.'}
                </p>
                {statusFilter && (
                  <Button variant="outline" className="tap mt-5 rounded-full" onClick={() => setStatusFilter(null)}>
                    Back to the open queue
                  </Button>
                )}
              </div>
            )
          ) : (
            <>
              {queueError && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm" role="alert" data-testid="queue-refresh-error">
                  <ShieldAlert className="h-4 w-4 shrink-0 text-danger" />
                  <p className="min-w-0 flex-1">
                    <span className="font-medium">Last refresh failed.</span>{' '}
                    <span className="text-muted-foreground">{queueError} Showing the last good queue.</span>
                  </p>
                  <Button variant="outline" size="sm" onClick={() => queueQuery.refetch()} className="tap rounded-full">Retry</Button>
                </div>
              )}
              <Card className="aurora-card overflow-hidden">
                <CardContent className="p-0">
                  <TableScroll mobile={mobileRows}>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="whitespace-nowrap pl-4">Ticket</TableHead>
                          <TableHead className="whitespace-nowrap">Component</TableHead>
                          <TableHead className="whitespace-nowrap">Severity</TableHead>
                          <TableHead className="whitespace-nowrap">Status</TableHead>
                          <TableHead className="whitespace-nowrap text-right">Users</TableHead>
                          <TableHead className="whitespace-nowrap text-right">Reports</TableHead>
                          <TableHead className="pr-4">Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tickets.map((t) => (
                          <TableRow
                            key={t.id}
                            className="cursor-pointer border-border transition-colors hover:bg-secondary/40"
                            onClick={() => setOpenTicketId(t.id)}
                            data-testid={`ticket-${t.id}`}
                          >
                            <TableCell className="whitespace-nowrap pl-4 font-mono text-primary">{t.id}</TableCell>
                            <TableCell className="min-w-0 max-w-[12rem]">
                              <span className="block truncate text-foreground/90">{t.component}</span>
                              <span className="block truncate text-xs text-muted-foreground">{t.errorCode}</span>
                            </TableCell>
                            <TableCell>
                              <Pill className={SEVERITY_STYLE[t.severity] || SEVERITY_STYLE.medium}>
                                {t.severity}
                              </Pill>
                            </TableCell>
                            <TableCell>
                              <Pill className={STATUS_STYLE[t.status] || MUTED_PILL}>
                                {t.status}
                              </Pill>
                            </TableCell>
                            <TableCell className="text-right font-semibold tnum">{t.affectedUsers}</TableCell>
                            <TableCell className="text-right text-muted-foreground tnum">{t.occurrences}</TableCell>
                            <TableCell className="min-w-[16rem] max-w-md pr-4">
                              <span className="line-clamp-2 text-sm text-foreground/90">{t.displayMessage}</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableScroll>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Detail: observed facts vs occurrence evidence, and the legal next steps only */}
      <Dialog open={!!openTicketId} onOpenChange={(o) => { if (!o) setOpenTicketId(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto rounded-2xl border-border bg-card sm:w-full sm:max-w-4xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-primary">{openTicketId}</DialogTitle>
            <DialogDescription>
              Internal detail — carries tenant identifiers; never paste it to a customer verbatim.
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isPending && (
            <div className="space-y-3 py-2" aria-busy="true">
              <div className="flex gap-2"><Bone className="h-5 w-20 rounded-full" /><Bone className="h-5 w-16 rounded-full" /></div>
              <Bone className="h-16 w-full" />
              <Bone className="h-24 w-full" />
              <p className="text-sm text-muted-foreground">Loading…</p>
            </div>
          )}
          {detailQuery.isError && (
            <div className="rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm text-danger break-words" role="alert">
              Could not load this ticket: {errText(detailQuery.error)}
            </div>
          )}
          {detail && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Pill className={STATUS_STYLE[detail.ticket.status] || ''}>{detail.ticket.status}</Pill>
                <Pill className={SEVERITY_STYLE[detail.ticket.severity] || ''}>{detail.ticket.severity}</Pill>
                <span className="min-w-0 break-words text-sm text-muted-foreground">
                  {detail.ticket.component} · {detail.ticket.errorCode}
                  {detail.ticket.fixLocation ? ` · fix: ${detail.ticket.fixLocation}` : ''}
                  {detail.linkedChats ? ` · ${detail.linkedChats} linked conversation(s)` : ''}
                </span>
              </div>

              <div>
                <div className={cn(LABEL, 'mb-1')}>Message</div>
                <pre className="whitespace-pre-wrap break-words rounded-lg bg-secondary/60 p-3 text-sm">{detail.ticket.displayMessage}</pre>
              </div>

              {detail.ticket.stackTrace && (
                <div className="min-w-0">
                  <div className={cn(LABEL, 'mb-1')}>Stack trace (obfuscated, as received)</div>
                  <pre className="max-w-full overflow-x-auto rounded-lg bg-secondary/60 p-3 text-xs">{detail.ticket.stackTrace}</pre>
                </div>
              )}

              <div>
                <div className={cn(LABEL, 'mb-1')}>
                  Affected customers ({detail.affectedEmails.length})
                </div>
                <div className="break-words text-sm text-foreground/90">{detail.affectedEmails.join(', ')}</div>
              </div>

              <div>
                <div className={cn(LABEL, 'mb-1')}>
                  Reports ({detail.occurrences.length}, newest first)
                </div>
                <div className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                  {detail.occurrences.map((o) => (
                    <div key={o.id} className="break-words px-3 py-2 text-xs text-muted-foreground">
                      <span className="text-foreground/90">{o.userEmail}</span>
                      {o.slotId ? ` · ${o.slotId}` : ''} · {o.seenAt}
                      {o.bcVersion ? ` · BC ${o.bcVersion}` : ''}
                      {o.kind ? ` · ${o.kind}` : ''}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className={cn(LABEL, 'mb-1')}>Move to</div>
                <div className="flex flex-wrap gap-1.5">
                  {(detail.legalTransitions || []).map((s) => (
                    <Button
                      key={s} size="sm" variant="outline" className="tap rounded-full"
                      disabled={transitionMutation.isPending}
                      onClick={() => {
                        if (s === 'fix-merged') {
                          // The release carrying the fix is what customers are later told to
                          // update to - record it at merge time, not never.
                          const v = window.prompt('Release that carries the fix (e.g. 1.4.2). '
                            + 'Leave empty to keep the previously recorded one.',
                            detail.ticket.fixedInVersion || '');
                          if (v === null) return; // cancelled
                          transitionMutation.mutate({ id: detail.ticket.id, status: s, fixedInVersion: v.trim() || undefined });
                          return;
                        }
                        if (s === 'duplicate') {
                          // A duplicate without a parent is a hole nothing can follow.
                          const d = window.prompt('Surviving ticket id this is a duplicate of (e.g. NM-7K3P):', '');
                          if (d === null || !d.trim()) return;
                          transitionMutation.mutate({ id: detail.ticket.id, status: s, duplicateOf: d.trim() });
                          return;
                        }
                        transitionMutation.mutate({ id: detail.ticket.id, status: s });
                      }}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground text-pretty">
                  Customers are notified on <b>deployed</b>, never on fix-merged. <b>verified</b> is
                  never offered here - it is reached only by the verification sweep, after the
                  fingerprint stays silent for the whole window while ingest is measuring.
                  {detail.isKbGap && <> A <b>knowledge gap</b> is answered in the review panel below, never moved to <b>answered</b> by hand.</>}
                </p>
              </div>

              {loopEnabled && (detail.isKbGap || detail.linkedChats > 0) && (
                <ReviewPanel ticketId={detail.ticket.id}
                             onChanged={() => { queryClient.invalidateQueries({ queryKey: ['support-queue'] }); queryClient.invalidateQueries({ queryKey: ['support-ticket'] }); }} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
